import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@heroui/react';
import { Trash, Warning } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { DocumentTemplate } from './types';

interface DeleteDocumentTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: DocumentTemplate | null;
  onSuccess: () => void;
}

const DeleteDocumentTemplateModal: React.FC<DeleteDocumentTemplateModalProps> = ({
  isOpen,
  onClose,
  template,
  onSuccess,
}) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    if (!template) return;

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/document-generation/templates/${template.id}`,
        {
          method: 'DELETE',
        },
        session
      );

      if (response.ok) {
        toast.success('Document sjabloon succesvol verwijderd');
        onSuccess();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Fout bij het verwijderen van het sjabloon');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Fout bij het verwijderen van het sjabloon');
    } finally {
      setIsLoading(false);
    }
  };

  if (!template) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalContent>
        <ModalHeader>
          <div className="flex items-center gap-2 text-danger">
            <Trash size={20} />
            Document Sjabloon Verwijderen
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <Warning size={24} className="text-warning" />
            </div>
            <div>
              <p className="text-neutral-primary mb-2">
                Weet je zeker dat je het volgende document sjabloon wilt verwijderen?
              </p>
              <div className="bg-neutral-50 p-3 rounded-lg mb-3">
                <p className="font-semibold text-neutral-primary">
                  {template.name}
                </p>
                <p className="text-sm text-neutral-secondaryMid">
                  {template.templateKey}
                </p>
                {template.description && (
                  <p className="text-sm text-neutral-secondaryMid mt-1">
                    {template.description}
                  </p>
                )}
              </div>
              <div className="text-sm text-danger bg-danger-50 p-3 rounded-lg">
                <p className="font-medium mb-1">Let op:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Deze actie kan niet ongedaan worden gemaakt</li>
                  <li>Het sjabloon wordt permanent verwijderd uit het systeem</li>
                  <li>Bestaande documenten die met dit sjabloon zijn gegenereerd blijven bestaan</li>
                  <li>Toekomstige document generaties met dit sjabloon zullen falen</li>
                </ul>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            Annuleren
          </Button>
          <Button
            color="danger"
            onPress={handleDelete}
            isLoading={isLoading}
            startContent={<Trash size={16} />}
          >
            Definitief Verwijderen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteDocumentTemplateModal;

